import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button, Card } from '@kawai/ui';
import { useAuth } from '../lib/auth.js';

export interface SaasAdminGuardProps {
  children: ReactNode;
}

/**
 * S-05: saas-admin app 専用の AuthGuard。
 *
 * tenant-reservation の AuthGuard との違い:
 * - 通すのは `role === 'saas-admin'` だけ
 * - tenant-admin / tenant-staff / 旧 admin / superadmin は弾く
 *   （誤ったテナントが saas-admin に来ても操作できないようにする）
 *
 * 状態:
 * - loading           : 「読み込み中…」
 * - signed-out        : /signin へ redirect（戻り先を state.from に保持）
 * - signed-in 非 saas : 「権限がありません（SaaS 管理者専用）」+ サインアウトボタン
 * - signed-in saas    : 子を描画
 *
 * S-137: 見た目は v2（白カード 30/26 / 枠線なし）。InvoicePrintPage のルートは `V2Scope` の外なので
 * Button には `v2` を明示する。
 */
export function SaasAdminGuard({ children }: SaasAdminGuardProps) {
  const { status, role, signOut, email } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-full items-center justify-center p-8 text-body text-ink-3"
      >
        読み込み中…
      </div>
    );
  }

  if (status === 'signed-out') {
    return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  }

  if (role !== 'saas-admin') {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12">
        <Card
          border="none"
          shadow="none"
          padding="none"
          className="w-full max-w-sm rounded-xl px-[30px] py-[26px] text-center"
        >
          <h1 className="text-h-card font-semibold text-ink">権限がありません</h1>
          <p className="mt-2 text-body text-ink-2">
            この画面は SaaS 管理者専用です。
            <br />
            {email ?? 'このアカウント'} には saas-admin 権限が付与されていません。
          </p>
          <Button
            type="button"
            v2
            fullWidth
            className="mt-4"
            onClick={() => {
              void signOut();
            }}
          >
            サインアウト
          </Button>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
